import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { fetchDelegations } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { emitter, ErrorHandler, formatAddress } from '@/helpers'
import { buildChangeOwnerMsg, ChangeOwnerError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

const sendSchema = z.object({
  recipientAddress: z.string().min(1, 'Recipient address is required'),
})

type SendFormValues = z.infer<typeof sendSchema>

export default function ChangeOwnerModal({
  open,
  onClose,
  nftAddress,
}: {
  open: boolean
  onClose: () => void
  nftAddress: string
}) {
  const { signAndBroadcast, address } = useKeplrWallet()
  const queryClient = useQueryClient()

  const form = useForm<SendFormValues>({
    resolver: zodResolver(sendSchema),
    mode: 'onChange',
    defaultValues: { recipientAddress: '' },
  })

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = form

  // Check delegation status to block sending delegated NFTs
  const { data: delegationData, isLoading: isLoadingDelegation } = useQuery({
    queryKey: queryKeys.staking.delegations(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => fetchDelegations(nftAddress),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const hasDelegation = Boolean(
    (delegationData?.delegation_responses ?? []).length > 0,
  )

  const sendTx = useMutation({
    mutationKey: queryKeys.tx.send(nftAddress),
    mutationFn: async (values: SendFormValues) => {
      if (!address) throw new Error('Wallet not connected')
      if (hasDelegation) throw new Error('NFT is delegated')

      const msg = buildChangeOwnerMsg({
        creator: address,
        address: nftAddress,
        recipient: values.recipientAddress,
        amount: {
          denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
          amount: '0', // legacy placeholder, not used by keeper
        },
      })

      const txHash = await signAndBroadcast([msg])
      return txHash
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Send submitted',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(ChangeOwnerError.fromUnknown(error))
    },
  })

  const isLoading = isSubmitting || sendTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isLoading && onClose()}
      title={<span className='text-foreground'>Send NFT (Change Owner)</span>}
      content={
        <form
          onSubmit={handleSubmit(values => sendTx.mutate(values))}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>NFT Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>
              Recipient Address
            </span>
            <GradientWrapper>
              <input
                value={watch('recipientAddress')}
                onChange={e =>
                  setValue('recipientAddress', e.target.value, {
                    shouldValidate: true,
                  })
                }
                className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none'
                placeholder='bridgeless1...'
              />
            </GradientWrapper>
            {errors.recipientAddress && (
              <span className='text-xs text-red-400'>
                {errors.recipientAddress.message as string}
              </span>
            )}
            {hasDelegation && (
              <span className='text-xs text-red-400'>
                NFT is delegated. Undelegate first.
              </span>
            )}
          </div>
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={isLoading || isLoadingDelegation || hasDelegation}
            onClick={handleSubmit(values => sendTx.mutate(values))}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Sending…
              </span>
            ) : (
              'Send'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
